import { getRoutePath } from 'config/routes';
import { jwtDecode } from 'jwt-decode';
import {
  IdTokenClaims,
  SigninRedirectArgs,
  User,
  UserManager,
  WebStorageStateStore,
} from 'oidc-client-ts';
import { TrackingEventProviderType } from 'services/tracking/types';
import { getLazyRootState } from 'store/lazyRootStoreAccessor';
import { CookieStorage } from 'utils/storage/CookieStorage';

const metadata = {
  authority: process.env.REACT_APP_OIDC_BASE_URL || '',
  client_id: process.env.REACT_APP_OIDC_CLIENT_ID || '',
  grant_types_supported: ['refresh_token'],
  scope: 'openid email profile',
};

const cookiePrefix = 'nf-oidc_';
const cookieLifetimeDays = 3;

export interface LoginTokens {
  token_type: string;
  id_token: string;
  access_token: string;
  login_type?: TrackingEventProviderType | undefined;
}

class AuthService {
  private readonly oidcManager: UserManager;
  private readonly userStateStore: WebStorageStateStore;
  private user: User | null = null;

  constructor() {
    this.userStateStore = new WebStorageStateStore({
      store: new CookieStorage({
        keyPrefix: cookiePrefix,
        expires: cookieLifetimeDays,
        path: '/',
        sameSite: 'lax',
      }),
    });

    this.oidcManager = new UserManager({
      ...metadata,
      redirect_uri: window.origin + getRoutePath('loginCallback'),
      //userStore: this.userStateStore,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    });
  }

  /**
   * Should OIDC auth be enabled at all?
   */
  public isEnabled(): boolean {
    return true;
  }

  /**
   * Should OIDC be enabled globally?
   * Otherwise, only allow it as an alternative.
   */
  public isGlobal(): boolean {
    return true;
  }

  public async initialize(): Promise<boolean> {
    const user = await this.oidcManager.getUser();
    if (user) {
      this.user = user;
    }
    return true;
  }

  public async loginRedirect(params: SigninRedirectArgs): Promise<void> {
    if (!params.prompt) {
      params = { ...params, prompt: 'select_account' };
    }
    return this.oidcManager.signinRedirect(params);
  }

  public async loginCallback(): Promise<User | null> {
    try {
      this.user = await this.oidcManager.signinRedirectCallback();
    } catch (e) {
      this.user = null;
    }
    return this.user;
  }

  public isAuthenticated(): boolean {
    return !!this.user;
  }

  public getAccessToken(): string {
    if (this.user === null || this.user.access_token === null) {
      throw new Error('Missing JWT access token');
    }
    return this.user.access_token;
  }

  public getAuthorizationHeaders(mustExist = false): object {
    if (!this.isAuthenticated()) {
      if (mustExist) {
        throw new Error('Missing JWT access token');
      }

      return {};
    }

    return {
      Authorization: 'Bearer ' + this.getAccessToken(),
      'X-WORKSPACE-ID': getLazyRootState().workspace.workspaceId,
    };
  }

  public logout() {
    this.user = null;

    try {
      this.oidcManager.signoutRedirect();
    } catch (e) {
      /* empty */
    }
  }

  public clearCredentials() {
    this.oidcManager.removeUser();
    this.user = null;
  }

  public async setTokens(tokens: LoginTokens) {
    this.user = new User({
      token_type: tokens.token_type,
      id_token: tokens.id_token,
      access_token: tokens.access_token,
      profile: jwtDecode<IdTokenClaims>(tokens.id_token),
    });
    await this.oidcManager.storeUser(this.user);
  }
}

export const authService = new AuthService();
